<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <div class="client-modal">
    <b-modal no-fade v-model="showDialog" size="xl" :ref="modalId" :id="modalId"
             no-close-on-backdrop scrollable
             modal-class="cmd-modal" content-class="cmd-content" body-class="cmd-body"
             header-class="cmd-header-wrap" footer-class="cmd-footer-wrap">

      <!-- Header: identity strip (fixed, body scrolls underneath) -->
      <template #modal-header>
        <div class="cmd-header" :class="{'is-new': isNewClient}">
          <div class="cmd-avatar">
            <feather-icon v-if="isNewClient" icon="UserPlusIcon" size="22"/>
            <span v-else>{{ initials }}</span>
          </div>
          <div class="cmd-header__text">
            <div class="cmd-header__eyebrow">
              {{ isNewClient ? $t('clientModal.newClient') : msg('Client') }}
              <span v-if="!isNewClient && client.id" class="cmd-header__id tabular">#{{ client.id }}</span>
            </div>
            <h4 class="cmd-header__name">{{ headerName }}</h4>
            <div class="cmd-header__meta" v-if="!isNewClient && loaded">
              <span class="cmd-chip" v-if="client.clientExt && client.clientExt.isOwner">
                <i class="fas fa-key"/>{{ $t('clientModal.roles.owner') }}
              </span>
              <span class="cmd-chip" v-if="client.clientExt && client.clientExt.renter">
                <i class="fas fa-bed"/>{{ $t('clientModal.roles.renter') }}
              </span>
              <span class="cmd-chip cmd-chip--ok" v-if="client.clientExt && client.clientExt.verified">
                <i class="fas fa-check"/>{{ $t('clientModal.roles.verified') }}
              </span>
              <span class="cmd-header__contact" v-if="client.email"><i class="far fa-envelope"/>{{ client.email }}</span>
              <span class="cmd-header__contact" v-if="client.phone"><i class="fas fa-phone"/>{{ client.phone }}</span>
            </div>
          </div>
          <button type="button" class="cmd-close" :title="$t('clientModal.close')" @click="showDialog = false">
            <feather-icon icon="XIcon" size="18"/>
          </button>
        </div>
      </template>

      <!--Panel content-->
      <div class="client-panel">
        <div v-if="loaded == true">
          <b-tabs class="cmd-tabs" nav-class="cmd-tabs__nav" content-class="cmd-tabs__content" v-model="tabIndex">
            <!-- Client details-->
            <b-tab @click="onTabChange(1)">
              <template #title><i class="fas fa-user"/>{{ $t('clientModal.tabs.profile') }}</template>
              <div class="cmd-pane">
                <clientDetails v-model="client"
                               v-bind:appointments="notExpiredAppointment"
                               @calendarUpdated="refreshAppointments"/>
              </div>
            </b-tab>
            <!-- Org Client details-->
            <b-tab @click="onTabChange(2)" class="clientOrg">
              <template #title><i class="fas fa-building"/>{{ $t('clientModal.tabs.administrative') }}</template>
              <div class="cmd-pane">
                <clientDetails v-model="client" :showClient="false" :showOrganization="true"/>
                <div class="cmd-section cmd-section--info mt-1" v-if="!isNewClient  &&  canOperateRep">
                  <div class="cmd-section__head">
                    <div class="cmd-section__icon cmd-section__icon--info"><i class="fas fa-user-tie"/></div>
                    <div>
                      <div class="cmd-section__title">{{ $t('clientModal.representatives') }}</div>
                      <div class="cmd-section__sub">{{ $t('clientModal.sub.representatives') }}</div>
                    </div>
                  </div>
                  <div class="cmd-section__body">
                    <representatives v-bind:clientId="client.id"
                                     v-bind:representatives="client.client2Users"
                                     :historyMaxHeight="280"/>
                  </div>
                </div>
              </div>
            </b-tab>
            <!-- Notes -->
            <b-tab @click="onTabChange(3)" v-if="!isNewClient && canReadNotes">
              <template #title>
                <b-spinner type="border" small variant="primary" v-if="tabsUpdating.notes === true" class="mr-50"/>
                <i v-else class="fas fa-note-sticky"/>{{ msg('Notes') }}
              </template>
              <div class="cmd-pane">
                <notes v-bind:clientId="client.id"
                       :client-name="client.firstname+' '+client.lastname"
                       @update-start="tabsUpdating.notes = true"
                       @update-finish="tabsUpdating.notes = false"></notes>
              </div>
            </b-tab>

            <!-- Monetary and bank           -->
            <b-tab @click="onTabChange(4)" v-if="!isNewClient && client.clientExt.isOwner">
              <template #title><i class="fas fa-building-columns"/>{{ $t('clientModal.tabs.bank') }}</template>
              <div class="cmd-pane">
                <clientDetails v-model="client" :showClient="false" :show-bank="true"/>
              </div>
            </b-tab>

            <!-- Document -->
            <b-tab @click="onTabChange(5)" v-if="!isNewClient && canReadDocuments">
              <template #title>
                <b-spinner type="border" small variant="primary" v-if="tabsUpdating.document == true" class="mr-50"/>
                <i v-else class="fas fa-folder-open"/>{{ $t('clientModal.tabs.documents') }}
              </template>
              <div class="cmd-pane cmd-pane--fixed">
                <client-documents v-bind:clientId="client.id"
                                  @update-start="tabsUpdating.document = true"
                                  @update-finish="tabsUpdating.document = false"></client-documents>
              </div>
            </b-tab>

            <b-tab @click="onTabChange(6)" v-if="!isNewClient">
              <template #title><i class="fas fa-calendar-check"/>{{ $t('clientModal.tabs.bookings') }}</template>
              <div class="cmd-pane">
                <clientDetails v-model="client" :showClient="false" :show-bookings="true" :bookings="bookings"/>
              </div>
            </b-tab>
            <b-tab @click="onTabChange(7)" v-if="!isNewClient">
              <template #title><i class="fab fa-whatsapp"/>{{ $t('clientModal.tabs.whatsapp') }}</template>
              <div class="cmd-pane">
                <whatsapp-chat-component v-bind:clientId="client.id" v-bind:whatsapp="client.whatsappChat"/>
              </div>
            </b-tab>
            <b-tab @click="onTabChange(8)" v-if="!isNewClient" lazy>
              <template #title><i class="fa-solid fa-timeline"/>{{ $t('clientActivity.tab') }}</template>
              <div class="cmd-pane">
                <client-activity-timeline :client-id="client.id" scrollable/>
              </div>
            </b-tab>
          </b-tabs>
        </div>
        <div v-else-if="loaded != true && error != null" class="cmd-state">
          <b-alert show variant="danger" class="mb-0">
            <i class="fas fa-exclamation-triangle mr-1"></i><strong>{{ error }}</strong>
          </b-alert>
        </div>
        <div v-else class="cmd-state cmd-state--loading">
          <b-spinner label="Spinning" variant="primary"/>
          <span>{{ msg('loading') }}</span>
        </div>
      </div>

      <!--Panel footer-->
      <template #modal-footer>
        <div class="cmd-footer">
          <span class="cmd-footer__hint" v-if="canSave">
            <i class="fas fa-circle-info"/>{{ $t('clientModal.saveHint') }}
          </span>
          <div class="cmd-footer__actions">
            <b-button variant="flat-secondary" @click="showDialog = false">{{ msg('Cancel') }}</b-button>
            <b-button variant="primary" class="cmd-save" @click="submit" v-if="canSave" :disabled="saveIsDisabled">
              <feather-icon icon="SaveIcon" size="15" class="mr-50"/>{{ msg('Save') }}
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>

  </div>

</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex'
import notes from '../panels/notes/notes'
import phoneRecords from '../panels/client/phoneRecords'
import representatives from '../panels/client/representatives'
import clientDetails from '../panels/client/clientDetails'
import {date, validation} from '../../global/helper'
import ClientDocuments from '../panels/client/clientDocuments'
import WhatsappChatComponent from "@/views/realEstate/contracts/WhatsappChatComponent.vue";

export default {
  name: 'clientModal',
  components: {
    ClientActivityTimeline: () => import('@/components/panels/client/ClientActivityTimeline.vue'),
    WhatsappChatComponent,
    ClientDocuments,
    notes,
    // eslint-disable-next-line vue/no-unused-components
    phoneRecords,
    representatives,
    clientDetails,

  },
  data() {
    return {
      uniqueId: 0,
      loaded: false,
      error: null,
      tabsUpdating: {
        notes: false,
        calls: false,
        treatments: false,
        objectives: false,
        progress: false,
        document: false,
      },
      tabIndex: 0,

      saveButton: 0,
      disabledSaveButton: {},

      clientCopy: {},
      appointments: [],
      timerActive: false,
      bookings: [],
      //Listeners
      listeners: {
        refresh: () => {
        },

      }
    }
  },
  props: {
    id: Number,
    dialog: {
      required: true,
      default: false,
      type: Boolean
    },
    canCreate: {
      required: false,
      default: false,
      type: Boolean
    },

  },
  watch: {
    id: function () { // watch it
      this.error = null
      this.tabIndex = 0
      this.disabledSaveButton = {}
      this.saveButton = 0
      this.onLoad()
    },
    dialog() {
      let $this = this

    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('clients', ['canOperateRep']),
    ...mapGetters('phone', ['canReadPhone']),
    ...mapGetters('notes', ['canReadNotes']),
    ...mapGetters('document', ['canReadDocuments']),
    ...mapGetters('data', ['canReadUsers', 'defaultCountry', 'defaultLang']),
    ...mapGetters('data', {
      _getAffiliateIdByCampaignId: 'getAffiliateIdByCampaignId',
      _getAffiliateIdByBannerId: 'getAffiliateIdByBannerId',
      _getCampaignByAffId: 'getCampaignByAffId',
      _getBanners: 'getBanners'
    }),
    ...mapState({
      data: function (store) {
        return store.data
      }
    }),
    client: {
      get() {
        return this.clientCopy
      },
      set(v) {
        if (v == null) {
          this.clientCopy = {
            id: 0,
            creationTime: this.$moment(new Date())
                .format('YYYY-MM-DD HH:mm'),
            lang: this.defaultLang,
            branchId: 0,
            clientExt: {},
            country: this.defaultCountry,
            representativeId: this.data.userId,
          }
          return
        }
        return this.clientCopy = v
      }
    },
    showDialog: {
      get() {
        return this.dialog == true
      },
      set(val) {
        this.$emit('update:dialog', (val == true))
      }
    },
    notExpiredAppointment() {

      if (this.appointments == null) {
        return []
      }

      if (this.isNewClient) {
        return []
      }

      return this.appointments.filter(p => Date.parse(p.start) > Date.now())

    },
    modalId() {
      return 'client___modal___' + this.uniqueId
    },
    isNewClient() {
      return this.id == null || isNaN(this.id) || this.id <= 0
    },
    /** Name shown in the header; falls back to company or email while a new client is being typed. */
    headerName() {
      const c = this.client || {}
      const name = [c.firstname, c.lastname].filter(p => p != null && String(p).trim() !== '').join(' ')
      if (name !== '') return name
      if (c.clientExt && c.clientExt.ownCompany) return c.clientExt.ownCompany
      if (c.email) return c.email
      return this.isNewClient ? this.$t('clientModal.newClient') : '—'
    },
    initials() {
      const c = this.client || {}
      const parts = [c.firstname, c.lastname].filter(p => p != null && String(p).trim() !== '')
      if (parts.length === 0) return (c.email || '?').charAt(0).toUpperCase()
      return parts.map(p => String(p).trim().charAt(0).toUpperCase()).join('').slice(0, 2)
    },
    hasAccount() {
      return this.id != null && this.id > 0 && this.loaded === true && this.client.account != null
    },
    hasPhone() {
      return this.id != null && this.id > 0 && this.loaded === true && this.client.phone != null
    },
    canSave() {
      return this.saveButton != null
    },
    saveIsDisabled() {
      return this.disabledSaveButton[this.saveButton] != null && this.disabledSaveButton[this.saveButton] !== true
    }

  },
  created() {
    this.uniqueId = this._uid
    this.onLoad()

    this.$root.$on('crm::calendar::refresh', (this.listeners.refresh = () => {
      this.loadClient()
    }))

  },
  beforeDestroy() {
    this.$root.$off('crm::calendar::refresh', this.listeners.refresh)

  },
  methods: {
    ...mapActions('clients', {
      _getClient: 'get',
      _updateClient: 'update',
      _register: 'register',
    }),
    ...mapActions('calendar', ['clientAppointments']),
    ...mapActions('realEstateContracts', ['getById', 'getAllByClients']),

    onLoad() {

      console.log('Client_modal -> onLoad,', {
        canCreate: this.canCreate === true,
        id: this.id
      })
      this.loaded = false
      this.client = null
      if (this.id == null) {
        this.loaded = true
        return

      }

      if (typeof this.id === 'number' && this.id > 0) {
        this.loadClient()

      } else {
        this.loaded = true
      }
    },
    dateStr(d) {
      return date.dateStringFormated(d, true)
    },
    loadClient() {
      let $this = this
      $this.loaded = false

      // Check if id is valid
      if (isNaN(this.id) || this.id <= 0) {
        this.error = this.msg('Invalid id ') + this.id
        return
      }

      // Fetch client and appointments
      this._getClient({ id: $this.id })
          .then(({ client, appointments }) => {
            // Refresh appointments
            $this.refreshAppointments()

            // After getting client, get affiliate details
            if (client.bannerid > 0 && (client.camId <= 0 || client.affId <= 0)) {
              let aff = this._getAffiliateIdByBannerId(client.bannerid)
              // Generate a new object with affiliate details
              $this.client = {
                ...client,
                affId: aff.affiliateId,
                camId: aff.campaignId
              }
            } else if (client.camId > 0 && client.affId <= 0) {
              let affId = this._getAffiliateIdByCampaignId(client.camId)
              $this.client = {
                ...client,
                affId
              }
            } else {
              $this.client = { ...client }
            }

            // Set page as loaded
            $this.loaded = true

            // Save appointments
            $this.appointments = appointments

            // Set default country and language if needed
            if ($this.client.country == null || $this.client.country <= 0) {
              $this.client.country = this.defaultCountry
            }
            if ($this.client.lang == null || $this.client.lang <= 0) {
              $this.client.lang = this.defaultLang
            }

            // Fetch all bookings after appointments are fetched
            return this.getAllByClients($this.id)
          })
          .then((ans) => {
            // Save bookings in this.bookings
            $this.bookings = ans["Contracts"]
          })
          .catch((error) => {
            // Handle errors
            $this.error = error
          })
    }
,
    refreshAppointments() {
      let $this = this
      this.clientAppointments({clientId: $this.id})
          .then(app => {
            if (app != null) {
              this.appointments = app
            }
          })
    },
    saveEvent() {
      this.$root.$emit('crm::submitCalendar')
    },
    onCalendarSubmitted(ans) {
      if (ans == null || ans.status === true) {
        this.dismissPopover()
      }
    },
    dismissPopover() {
      this.$root.$emit('bv::hide::popover')
    },
    onTabChange(tabIndex) {

      this.saveButton = tabIndex
      switch (tabIndex) {
        case 3:
          this.$root.$emit('crm::client::note::load')
          this.saveButton = null
          break
        case 5:
          this.$root.$emit('crm::client::documents::load')
          this.saveButton = null
          break
        case 6: // bookings, whatsapp, activity: nothing to save on these tabs
        case 7:
        case 8:
          this.saveButton = null
          break
      }
    },
    submit(e) {
      console.log('submit1')
      if (this.saveButton <= 2 || this.saveButton === 4) { // profile update
        if (typeof e === 'object' && typeof e.preventDefault === 'function') {
          e.preventDefault()
        }

        console.log('submit2')

        //TODO: CHECK IT
        /*if (this.$v.$invalid === true)
            return;*/
        console.log('submit3')

        if (!isNaN(this.client.id) && this.client.id > 0) {
          return this.updateClient()
        }
        return this.createClient()

      }

    },
    createClient() {
      console.log('updateClient()')
      let $this = this
      this._register({client: this.client})
          .then(
              (a) => {
                $this.$emit('newClient', a)
              },
              () => $this.$emit('newClient', 0)
          )
    },
    updateClient() {
      let $this = this
      this._updateClient({
        id: this.client.id,
        client: this.client
      })
          .then(
              () => {
                $this.$root.$emit('crm::client-table::refresh')
                $this.$emit('clientUpdated', true)

              },
              () => $this.$emit('clientUpdated', false)
          )
    },

    closeDialog() {
      this.$emit('closeDialog')
    },

  },
}
</script>

<style lang="scss">
/* Unscoped on purpose: the b-modal content is portaled to <body>, so everything
   is keyed to the content-class (.cmd-content) — same approach as AppointmentModal. */
.cmd-content {
  border-radius: 14px;
  overflow: hidden;
  border: 0;
  box-shadow: 0 24px 60px rgba(34, 41, 47, 0.28);
}
.cmd-content .cmd-header-wrap,
.cmd-content .cmd-footer-wrap {
  padding: 0;
  border: 0;
  display: block;
}

/* ── Header ── */
.cmd-content .cmd-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.4rem;
  background: linear-gradient(120deg, #7367f0, #4b4b9b);
  color: #fff;
}
.cmd-content .cmd-header.is-new { background: linear-gradient(120deg, #28c76f, #1f7a5a); }
.cmd-content .cmd-avatar {
  width: 52px; height: 52px; flex: 0 0 52px;
  border-radius: 14px;
  background: rgba(255, 255, 255, .18);
  border: 1px solid rgba(255, 255, 255, .28);
  display: flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 1.15rem; letter-spacing: .5px;
}
.cmd-content .cmd-header__text { min-width: 0; flex: 1; }
.cmd-content .cmd-header__eyebrow {
  font-size: 10.5px; font-weight: 800; letter-spacing: .6px; text-transform: uppercase;
  opacity: .8; display: flex; align-items: center; gap: .5rem;
}
.cmd-content .cmd-header__id {
  background: rgba(255, 255, 255, .16); border-radius: 6px; padding: 1px 7px; letter-spacing: 0;
}
.cmd-content .cmd-header__name {
  color: #fff; font-weight: 800; margin: 2px 0 0; font-size: 1.25rem;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cmd-content .cmd-header__meta {
  display: flex; flex-wrap: wrap; align-items: center; gap: .4rem .75rem; margin-top: .4rem;
  font-size: .8rem;
}
.cmd-content .cmd-header__contact { opacity: .9; i { margin-right: .3rem; opacity: .8; } }
.cmd-content .cmd-chip {
  display: inline-flex; align-items: center; gap: .3rem;
  font-size: 10.5px; font-weight: 800; letter-spacing: .4px; text-transform: uppercase;
  padding: 2px 8px; border-radius: 7px;
  background: rgba(255, 255, 255, .18); color: #fff;
}
.cmd-content .cmd-chip--ok { background: rgba(40, 199, 111, .35); }
.cmd-content .cmd-close {
  margin-left: auto; flex: 0 0 auto;
  width: 36px; height: 36px; border-radius: 10px; border: 0;
  background: rgba(255, 255, 255, .14); color: #fff;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  transition: background .15s ease;
  &:hover { background: rgba(255, 255, 255, .26); }
}

/* ── Body & tabs ── */
.cmd-content .cmd-body { padding: 0; background: #f8f8fb; }
.cmd-content .cmd-tabs__nav {
  display: flex; flex-wrap: wrap; gap: .35rem;
  padding: .85rem 1.4rem 0; margin: 0;
  border-bottom: 1px solid rgba(34, 41, 47, .08);
  background: #fff;
  position: sticky; top: 0; z-index: 5;
}
.cmd-content .cmd-tabs__nav .nav-item { margin: 0; }
.cmd-content .cmd-tabs__nav .nav-link {
  display: inline-flex; align-items: center; gap: .45rem;
  border: 0 !important; border-radius: 9px 9px 0 0;
  padding: .6rem .95rem; font-weight: 600; font-size: .85rem; color: #6e6b7b;
  background: transparent; position: relative;
  i { font-size: .85rem; opacity: .75; }
  &::after { display: none !important; }
  &:hover { color: #7367f0; background: rgba(115, 103, 240, .06); }
  &.active {
    color: #7367f0; background: rgba(115, 103, 240, .10);
    box-shadow: inset 0 -2px 0 #7367f0;
    i { opacity: 1; }
  }
}
.cmd-content .cmd-tabs__content { padding: 0; }
.cmd-content .cmd-pane { padding: 1.25rem 1.4rem 1.5rem; min-height: 60vh; }
.cmd-content .cmd-pane--fixed { height: 70vh; overflow: auto; }

/* Section card (shared look with clientDetails' .cd-section) */
.cmd-content .cmd-section {
  background: #fff;
  border: 1px solid rgba(34, 41, 47, 0.08);
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(34, 41, 47, 0.05);
  overflow: hidden;
}
.cmd-content .cmd-section--info { border-top: 3px solid #00bcd9; }
.cmd-content .cmd-section--info .cmd-section__head { background: rgba(0, 207, 232, 0.07); }
.cmd-content .cmd-section__head {
  display: flex; align-items: center; gap: 12px; padding: 14px 18px;
  border-bottom: 1px solid rgba(34, 41, 47, 0.06);
}
.cmd-content .cmd-section__icon {
  flex-shrink: 0; width: 40px; height: 40px; border-radius: 11px; color: #fff;
  display: flex; align-items: center; justify-content: center;
}
.cmd-content .cmd-section__icon--info { background: #00bcd9; box-shadow: 0 4px 10px rgba(0, 207, 232, 0.40); }
.cmd-content .cmd-section__title { font-size: 14.5px; font-weight: 800; color: #2f2d3d; }
.cmd-content .cmd-section__sub { font-size: 11.5px; color: #7c7a89; font-weight: 500; margin-top: 1px; }
.cmd-content .cmd-section__body { padding: 16px 18px; }

/* Loading / error */
.cmd-content .cmd-state { padding: 2rem 1.4rem; min-height: 40vh; }
.cmd-content .cmd-state--loading {
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: .75rem;
  color: #7367f0; font-weight: 600;
}

/* ── Footer ── */
.cmd-content .cmd-footer {
  display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  padding: .85rem 1.4rem;
  background: #fff; border-top: 1px solid rgba(34, 41, 47, .08);
}
.cmd-content .cmd-footer__hint { font-size: .8rem; color: #7c7a89; i { margin-right: .35rem; color: #7367f0; } }
.cmd-content .cmd-footer__actions { display: flex; gap: .5rem; margin-left: auto; }
.cmd-content .cmd-save { min-width: 150px; box-shadow: 0 4px 12px rgba(115, 103, 240, .35); }

/* ── Dark layout ── */
.dark-layout .cmd-content { box-shadow: 0 24px 60px rgba(0, 0, 0, .55); }
.dark-layout .cmd-content .cmd-body { background: #161d31; }
.dark-layout .cmd-content .cmd-tabs__nav { background: #283046; border-bottom-color: rgba(255, 255, 255, .08); }
.dark-layout .cmd-content .cmd-tabs__nav .nav-link { color: #b4b7bd; }
.dark-layout .cmd-content .cmd-tabs__nav .nav-link:hover { background: rgba(115, 103, 240, .12); color: #9e95f5; }
.dark-layout .cmd-content .cmd-tabs__nav .nav-link.active { background: rgba(115, 103, 240, .18); color: #9e95f5; }
.dark-layout .cmd-content .cmd-section {
  background: #283046; border-color: rgba(255, 255, 255, .07);
  box-shadow: 0 2px 12px rgba(0, 0, 0, .25);
}
.dark-layout .cmd-content .cmd-section__head { border-bottom-color: rgba(255, 255, 255, .08); }
.dark-layout .cmd-content .cmd-section--info .cmd-section__head { background: rgba(0, 207, 232, .16); }
.dark-layout .cmd-content .cmd-section__title { color: #d0d2d6; }
.dark-layout .cmd-content .cmd-section__sub { color: #9b9aa8; }
.dark-layout .cmd-content .cmd-footer { background: #283046; border-top-color: rgba(255, 255, 255, .08); }
.dark-layout .cmd-content .cmd-footer__hint { color: #9b9aa8; }
</style>
